
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Question, QuestionType, isMultipleChoiceContent, MarkschemeType } from "@/types/test";
import { Check, FileTextIcon } from "lucide-react";
import { MarkschemeDialog } from "@/components/shared/MarkschemeDialog";

const toUIQuestionType = (type: QuestionType): string => {
  switch (type) {
    case "multiple_choice":
      return "Multiple Choice";
    case "essay":
      return "Essay";
    case "audio_response":
      return "Audio Recording";
    case "video_response":
      return "Video Recording";
    case "coding":
      return "Coding";
    default:
      return type;
  }
};

interface AssessmentPreviewProps {
  questions: Question[];
  selectedQuestions?: Set<number>;
  editingQuestionIndex?: number | null;
  onToggleQuestion?: (index: number) => void;
  onQuestionUpdate?: (questionId: string, updates: any) => void;
  onEditQuestion?: (index: number) => void;
  onRegenerateQuestions?: () => void;
}

export const AssessmentPreview = ({ 
  questions,
  selectedQuestions,
  editingQuestionIndex,
  onToggleQuestion,
  onQuestionUpdate,
  onEditQuestion,
  onRegenerateQuestions 
}: AssessmentPreviewProps) => {
  const [openMarkschemeIndex, setOpenMarkschemeIndex] = useState<number | null>(null);

  const handleOpenMarkscheme = (index: number) => {
    setOpenMarkschemeIndex(index);
  };

  const handleCloseMarkscheme = () => {
    setOpenMarkschemeIndex(null);
  };

  const handleMarkschemeUpdate = (questionId: string, markscheme: MarkschemeType) => {
    if (onQuestionUpdate) {
      onQuestionUpdate(questionId, { 
        markscheme, 
        marks: markscheme.total_points || 1
      });
    }
    setOpenMarkschemeIndex(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assessment Questions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {questions.map((question, index) => {
          const content = typeof question.content === 'string' 
            ? JSON.parse(question.content) 
            : question.content;
            
          const isMultipleChoice = question.question_type === "multiple_choice" && 
                                   content.options && 
                                   content.correctOption !== undefined;
          
          return (
            <div key={question.id} className="border rounded-md p-4 bg-card shadow-sm">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-base font-medium text-foreground mb-1">Question {index + 1}</h4>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">{toUIQuestionType(question.question_type)}</Badge>
                    
                    {question.skill && (
                      <Badge variant="outline" className="text-xs bg-muted/30">
                        {question.skill}
                      </Badge>
                    )}
                    
                    {question.difficulty && (
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          question.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          question.difficulty === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}
                      >
                        {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
                      </Badge>
                    )}
                    
                    {question.marks && (
                      <Badge variant="outline" className="text-xs bg-muted/30">
                        {question.marks} {question.marks === 1 ? "mark" : "marks"}
                      </Badge>
                    )}
                    
                    {question.time_limit && (
                      <Badge variant="outline" className="text-xs bg-muted/30">
                        {question.time_limit} seconds
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div>
                  <p className="text-foreground font-medium mb-3 mt-2">
                    {content && typeof content === 'object' && 'question' in content 
                      ? content.question 
                      : 'Invalid question format'}
                  </p>
                  
                  {isMultipleChoice && content.options && (
                    <div className="space-y-2 mt-4">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Options:</p>
                      <div className="space-y-2">
                        {content.options.map((option: string, optIndex: number) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            {optIndex === content.correctOption && (
                              <Check className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />
                            )}
                            <span className={`text-sm ${optIndex === content.correctOption ? "text-green-600 dark:text-green-500 font-medium" : "text-foreground"}`}>
                              {option}
                            </span>
                            {optIndex === content.correctOption && (
                              <span className="text-xs text-green-600 dark:text-green-500 ml-1">(Correct answer)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleOpenMarkscheme(index)} 
                    className="text-xs flex items-center gap-1"
                  >
                    <FileTextIcon className="h-3 w-3" />
                    {question.markscheme ? "View Markscheme" : "Create Markscheme"}
                  </Button>
                  
                  <MarkschemeDialog
                    questionNumber={index + 1}
                    markscheme={question.markscheme}
                    open={openMarkschemeIndex === index}
                    onOpenChange={(open) => !open && handleCloseMarkscheme()}
                    onSave={(markscheme) => handleMarkschemeUpdate(question.id, markscheme)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
